import React, { useEffect, useState } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Button, Stack, TextField } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { openPopup } from "../../stateManagement/actions/popupActions";
import useSessionStorageDates from "../../components/dms_common/UseSessionStorageDates";
import { getDayEndStatusFromSession } from "../../components/dms_common/DayEndUtils";


interface DateFilterProps {
  onApply: Function;
  onReset: Function;
  onUpdate?: Function;
  initialDayOffset?: number; // Number of days to subtract from current date for start date (0 means today)
  endDayOffset?: number; // Number of days to subtract from current date for end date (0 means today)
  maxDays?: number; // Maximum allowed difference between start and end date
  futureDateAllowed?: boolean; 
  featureId?: string;
  pageId?: string;
  dayEndCheck?: boolean;
}

const DateFilter = ({ 
  onApply, 
  onReset, 
  onUpdate,
  initialDayOffset = 7, 
  endDayOffset = 0,
  maxDays = 30,
  futureDateAllowed = false,
  featureId,
  pageId,
  dayEndCheck = false
}: DateFilterProps) => {
  const defaultStartDate = dayjs().subtract(initialDayOffset, 'day');
  const defaultEndDate = dayjs().subtract(endDayOffset, 'day');
  const { storedStartDate, storedEndDate, storeSessionStorageDates } = useSessionStorageDates(featureId + "_dates", defaultStartDate, defaultEndDate);

  let initialStartDate, initialEndDate;
  
  const dayEndObj = getDayEndStatusFromSession();

  if (dayEndCheck && dayEndObj && dayEndObj.isDayEndInProgress) {
    initialStartDate = dayjs(dayEndObj.startDate);
    initialEndDate = dayjs(dayEndObj.endDate);
  }
  else {
    initialStartDate = defaultStartDate;
    initialEndDate = defaultEndDate;
  }


  const [startDate, setStartDate] = useState<Dayjs>(featureId ? storedStartDate : initialStartDate);
  const [endDate, setEndDate] = useState<Dayjs>(featureId ? storedEndDate : initialEndDate);
  const handleApply = () => {
    if (startDate && endDate) {
      // Check if date range is valid
      if (!startDate.isSameOrBefore(endDate)) {
        openPopup("Error", "To Date should be greater than from date.");
        return;
      }
      
      // Check if date range exceeds max days
      if (endDate.diff(startDate, 'day') > maxDays) {
        openPopup("Error", `Date range cannot exceed ${maxDays} days.`);
        return;
      }
      onApply(startDate, endDate);
      onUpdate && onUpdate(startDate, endDate);
    } else {
      openPopup("Error", "Invalid date range! Ensure both dates are selected.");
    }
  };

  const handleReset = () => {
    const initialStartDate =  dayjs().subtract(initialDayOffset, 'day');
    const initialEndDate = dayjs().subtract(endDayOffset, 'day');
    setStartDate(initialStartDate);
    setEndDate(initialEndDate);
    onReset();
    onUpdate && onUpdate(initialStartDate, initialEndDate);
  };

  useEffect(() => {
    featureId && pageId && storeSessionStorageDates(startDate, endDate);
  }, [startDate, endDate]);


  return (
    <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
      <Stack spacing={2.8} direction={"row"}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="From Date"
            inputFormat="DD-MM-YYYY"
            value={startDate}
            maxDate={!futureDateAllowed ? dayjs() : undefined}
            onChange={(value) => value && setStartDate(value)}
            renderInput={(props) => <TextField {...props} size="small" />}
            disabled={dayEndObj && dayEndObj.isDayEndInProgress}
          />
        </LocalizationProvider>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="To Date"
            inputFormat="DD-MM-YYYY"
            value={endDate}
            maxDate={!futureDateAllowed ? dayjs() : undefined}
            onChange={(value) => value && setEndDate(value)}
            renderInput={(props) => <TextField {...props} size="small" />}
            disabled={dayEndObj && dayEndObj.isDayEndInProgress}
          />
        </LocalizationProvider>
      </Stack>
      <Button
        variant="contained"
        onClick={handleApply}
        sx={{
          textTransform: "none",
          backgroundColor: "#237C9D",
          "&:hover": {
            backgroundColor: "#1B5E77",
          },
        }}
        disabled={dayEndObj && dayEndObj.isDayEndInProgress}
      >
        Apply
      </Button>
      <Button
        variant="outlined"
        onClick={handleReset}
        sx={{
          textTransform: "none",
          borderColor: "#237C9D",
          color: "#237C9D",
          "&:hover": {
            backgroundColor: "#E6F2F6",
          },
        }}
        disabled={dayEndObj && dayEndObj.isDayEndInProgress}
      >
        Reset
      </Button>
    </div>
  );
};

export default DateFilter;
